import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import axios from "axios";
import styles from "./BusinessStep.module.css";
import { trackEvent } from "../../lib/analytics";
import GoogleAddressInputWithDropdown from "../GoogleAddressInputWithDropdown/GoogleAddressInputWithDropdown";
import { secureStorage } from "../../utils/secureStorage";
import { listSiteMap, validateWebsite } from "../../Store/apiStore";
import filterCompanyPages from "../../utils/companyKeyboards";
import { classifyBusinessCategoryWithSource } from "../../lib/classifyBusinessCategory";
import { usesVobiz } from "../../utils/apiBackend";

// The very first field of onboarding opened on the US flag and "+1" on the
// India site too, so an Indian owner had to change the country before they
// could type their own number. Same India/US switch the rest of the flow runs
// on, read once — the site cannot change mid-session.
const DEFAULT_COUNTRY = usesVobiz() ? "in" : "us";
const DEFAULT_DIAL_CODE = usesVobiz() ? "91" : "1";
const PHONE_PLACEHOLDER = usesVobiz()
  ? "+91 98765 43210"
  : "+1 469) 489-1061";

const StepPhoneLookup = forwardRef(({ data, updateFields, onSkip }, ref) => {
  // Determine initial view mode
  const getInitialViewMode = () => {
    if (data.placeDetails?.placeId) {
      return "found";
    }
    if (data.noGoogleListing === true) {
      return "manual";
    }
    return "lookup";
  };

  const [viewMode, setViewMode] = useState(getInitialViewMode());
  const [phone, setPhone] = useState(data.placeDetails?.phone || "");
  const [dialCode, setDialCode] = useState(DEFAULT_DIAL_CODE);
  const [manualDialCode, setManualDialCode] = useState(DEFAULT_DIAL_CODE);
  // User ne flag dropdown se jo country chuni. Ye state isliye chahiye kyunki
  // PhoneInput ko `country` hardcoded "us" dene par wo har re-render pe country
  // ko phone value se DOBARA guess karta hai — aur "+1" par US aur Canada dono
  // match karte hain, jisme US ki priority upar hai. Isliye Canada chunne ke
  // baad bhi flag US ka aa jata tha. Ab country hum control karte hain.
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [manualSelectedCountry, setManualSelectedCountry] =
    useState(DEFAULT_COUNTRY);

  // PhoneInput ke `value` me "+" zaroori hai. Library check karti hai ki value
  // "+<dialCode>" se shuru hoti hai ya nahi: agar haan to sirf format karti hai,
  // agar nahi to country dobara guess karti hai (aur +1 pe US jeet jata hai).
  // State me hum digits hi rakhte hain — "+" sirf yahan display ke liye lagta hai.
  const withPlus = (v) => {
    const s = String(v || "");
    if (!s) return "";
    return s.startsWith("+") ? s : `+${s}`;
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [businessResult, setBusinessResult] = useState(data.placeDetails || null);

  // Manual form state
  const [website, setWebsite] = useState(data.placeDetails?.website || data.businessUrl || "");
  const [noWebsite, setNoWebsite] = useState(data.noBusinessWebsite || false);
  const [name, setName] = useState(data.placeDetails?.businessName || data.displayBusinessName || "");
  const [businessPhone, setBusinessPhone] = useState(data.placeDetails?.phone || data.businessPhone || "");
  const [address, setAddress] = useState(data.placeDetails?.address || "");
  const [errors, setErrors] = useState({});

  // Website verification state
  const [isVerifying, setIsVerifying] = useState(false);
  const [isUrlValid, setIsUrlValid] = useState(null);
  const typingTimeoutRef = useRef(null);

  const addressInputRef = useRef(null);

  // ── Manual form ka phone field — mobile pe focus/tap hone par scroll ──
  // Why: phone form ka last field hai. Mobile keyboard khulte hi ye keyboard
  // ke peeche chala jata hai aur user ko apna type kiya number dikhta hi nahi.
  // Focus par field ko viewport ke center mein le aate hain.
  const manualPhoneFieldRef = useRef(null);

  const isMobileViewport = () =>
    typeof window !== "undefined" &&
    (window.matchMedia?.("(max-width: 768px)")?.matches || window.innerWidth <= 768);

  const scrollManualPhoneIntoView = () => {
    if (!isMobileViewport()) return;
    const wrapper = manualPhoneFieldRef.current;
    if (!wrapper) return;
    // Delay: keyboard animation (~250-300ms) settle hone ke baad scroll karo,
    // warna browser purani viewport height pe scroll kar deta hai.
    setTimeout(() => {
      wrapper.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
  };

  // ── Step 1 ka "Business phone number" — tap/focus par upar le aao ──
  // Mobile pe keyboard khulte hi field neeche dab jaata hai. Focus par field
  // (label samet) ko screen ke upar scroll kar dete hain. Ye screen chhoti
  // hai, to scroll karne ko jagah hi nahi hoti — isliye focus ke dauraan
  // neeche ka spacer lamba kar dete hain (phoneFocused), blur par wapas.
  const lookupPhoneGroupRef = useRef(null);
  const [phoneFocused, setPhoneFocused] = useState(false);
  const isLookupPhoneInput = (el) => el?.tagName === "INPUT" && el.type === "tel";
  const handleLookupPhoneFocus = (e) => {
    if (!isMobileViewport() || !isLookupPhoneInput(e.target)) return;
    setPhoneFocused(true);
    // Delay: spacer lamba ho jaaye aur keyboard khul jaaye, phir scroll.
    setTimeout(() => {
      lookupPhoneGroupRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  };
  const handleLookupPhoneBlur = (e) => {
    // Field ke andar hi focus ghoome (jaise country dropdown) to kuch nahi.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setPhoneFocused(false);
  };

  // Fetch the business website's sitemap so the agent is trained on the site's
  // pages — same logic the old GMB-listing path (BusinessStep.handleListingSelected)
  // used. WITHOUT this, formData.sitemapUrls / selectedSitemapUrls stay empty and
  // createAgent builds NO website knowledge base. Best-effort, never blocks.
  const fetchAndSetSitemap = async (rawWebsite) => {
    const site = (rawWebsite || "").trim();
    if (!site) return;
    try {
      const res = await listSiteMap(site);
      let urls = res?.urls || [];
      if (!urls.includes(site)) urls.unshift(site);
      const pages = await filterCompanyPages(urls);
      const first10 = (Array.isArray(pages) ? pages : urls).slice(0, 10);
      const selected = first10.map((u) => ({ url: u, checkedStatus: true }));
      updateFields({ sitemapUrls: first10, selectedSitemapUrls: selected });
      secureStorage.set("urls", first10);
      secureStorage.set("selectedSiteMapUrls", selected);
      secureStorage.set("selectedSiteMapUrls_backup", selected);
      secureStorage.set("viewSiteMap", true);
    } catch (e) {
      console.error("StepPhoneLookup: sitemap fetch failed:", e?.message);
    }
  };

  // Ask Gemini which of OUR categories (lib/businessCategories) best matches the
  // found business, and pre-select it so the category step is auto-filled. Best-
  // effort — never blocks the flow if Gemini is slow / the key is missing.
  const classificationPromiseRef = useRef(null);

  /*
   * Category chun li gayi — GA ko batao.
   *
   * `business_category_selected` DONO raaston se jata hai: yahan se jab Gemini
   * khud category pick karta hai, aur StepOne se jab user apne haath se
   * chunta hai. Pehle auto-pick sirf `business_category_auto_selected` bhejta
   * tha, isliye "Business Category Selected" wala event auto wale users ke
   * liye kabhi hit hi nahi hota tha — aur unhi users me category screen skip
   * bhi ho jati hai, to wahan se bhi kabhi nahi aata tha.
   *
   * `source` batata hai kaunsa raasta tha. StepOne wahi keys bhejta hai
   * (category / subtype / source) — schema dono taraf ek jaisa.
   *
   * Dono raaste mutually exclusive hain: website ho to Gemini pick karta hai
   * aur category screen skip ho jati hai; website na ho to screen dikhti hai
   * aur user khud chunta hai. Isliye trackEvent ka once-guard yahan sahi
   * hai — ek user ko ek hi baar milega.
   */
  const trackCategoryChosen = (category, source) => {
    trackEvent("business_category_selected", {
      category: category || "",
      subtype: "",
      source,
    });
    // Purani reports isi naam par bani hain — chalu rakha.
    trackEvent("business_category_auto_selected", { category: category || "", source });
  };

  /*
   * Auto-pick nakaam — category ko KHAALI chhodo.
   *
   * Ye zaroori hai: BusinessStep step 1 ke baad decide karta hai ki category
   * screen dikhani hai ya skip karni hai, aur wo faisla isi khaali/bhare hone
   * par hota hai. Koi fallback category set kar denge to screen skip ho jayegi
   * aur user ko galat category mil jayegi.
   *
   * `business_category_selected` yahan se NAHI jata — kuch select hua hi nahi.
   * Wo event ab StepOne se aayega jab user screen par khud chunega. Yahan sirf
   * ek failure event, taaki pata chale auto-pick kitni baar aur kahan fail ho
   * raha hai.
   */
  const clearAutoCategory = (reason) => {
    trackEvent("business_category_auto_failed", { reason });

    /*
     * User ki KHUD CHUNI hui category ko kabhi mat mitao.
     *
     * Dots se aage-peeche navigate karne par step 1 ki validate() dobara chalti
     * hai, jo auto-pick dobara chalati hai — aur wo phir se fail hoti hai. Pehle
     * ye yahan pahunch kar wo category mita deta tha jo user category screen par
     * khud chun chuka tha. Nateeja: screen par selection to highlighted dikhta
     * tha (StepOne ka apna state), par formData.businessType khaali ho jata tha
     * aur Continue disable ho jata tha.
     *
     * `categoryAutoPicked` batata hai category kahan se aayi: true = auto-pick,
     * warna user ne chuni thi (goToStep step 3 par jate waqt ise hata deta hai).
     */
    const existing = String(secureStorage.get('businessType') || '').trim();
    const wasAutoPicked = secureStorage.get('categoryAutoPicked', false) === true;
    if (existing && !wasAutoPicked) return;

    updateFields({ businessType: "", categoryAutoPicked: false });
    secureStorage.remove('businessType');
    secureStorage.remove('categoryAutoPicked');
  };

  const autoPickCategory = async (biz) => {
    const promise = (async () => {
      try {
        if (noWebsite) {
          // Website hi nahi hai — classify chalane ko kuch hai hi nahi. Ye
          // failure nahi, jaan-boojh kar skip hai; par event yahan bhi jata hai
          // taaki "auto-pick se category nahi mili" wale SAARE cases ek hi
          // event me reason ke saath gine ja sakein.
          clearAutoCategory("skipped_no_website");
          return null;
        }

        // `source` batata hai category KAHAN SE aayi — local keyword match se
        // ya backend/Gemini se. Pehle yahan sirf category milti thi aur hum
        // sab kuch "gemini" gin lete the, chahe Gemini call hui hi na ho.
        const { category: cat, source: classifySource } =
          await classifyBusinessCategoryWithSource({
            name: biz?.businessName || biz?.name,
            categories: biz?.categories || biz?.types,
            website: biz?.website,
            address: biz?.address,
          });
        console.log(cat, classifySource)
        if (cat) {
          updateFields({ businessType: cat, categoryAutoPicked: true });
          secureStorage.set('businessType', cat);
          secureStorage.set('categoryAutoPicked', true);
          trackCategoryChosen(cat, classifySource);
          return cat;
        }
        // Na local keyword match se mili, na backend/Gemini se. Pehle yahan
        // chupchaap "E-commerce" thop diya jata tha — user ka business kuch bhi
        // ho, agent galat category par ban jata tha aur use pata bhi nahi
        // chalta. Ab category KHAALI chhodte hain, jisse BusinessStep category
        // wali screen dikha deta hai aur user khud chunta hai.
        clearAutoCategory(`unresolved_${classifySource}`);
        return null;
      } catch (e) {
        console.error("autoPickCategory failed:", e);
        // Network / JS error — yahan bhi guess mat lagao, screen dikhao.
        clearAutoCategory("classify_error");
        return null;
      }
    })();

    classificationPromiseRef.current = promise;
    return await promise;
  };

  // Expose validate() to parent (BusinessStep) via ref — this lets handleContinue
  // await the Gemini classification before deciding skip-category vs show-category.
  useImperativeHandle(ref, () => ({
    validate: async () => {
      // If classification is still running, wait for it (max ~5s timeout)
      if (classificationPromiseRef.current) {
        try {
          await Promise.race([
            classificationPromiseRef.current,
            new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 5000)),
          ]);
        } catch (e) {
          console.warn("StepPhoneLookup validate: classification wait issue:", e?.message);
        }
      }
      return true; // Step 1 validation always passes (phone lookup itself has inline validation)
    },
  }));
  const extractAddressComponents = (addressComponents) => {
    if (!Array.isArray(addressComponents)) return {};
    const getComponent = (primaryType, fallbackType = null, useShort = false) => {
      const comp = addressComponents.find(
        (c) => c.types.includes(primaryType) || (fallbackType && c.types.includes(fallbackType))
      );
      return comp ? (useShort ? comp.short_name : comp.long_name) : '';
    };
    return {
      city: getComponent('locality', 'sublocality_level_1'),
      state: getComponent('administrative_area_level_1'),
      state_code: getComponent('administrative_area_level_1', null, true),
      country: getComponent('country'),
      country_code: getComponent('country', null, true),
      postal_code: getComponent('postal_code'),
      street_number: getComponent('street_number'),
      route: getComponent('route'),
    };
  };

  const [extractedAddressFields, setExtractedAddressFields] = useState({});

  const handleAddressChange = async (selectedAddress, placeId) => {
    setAddress(selectedAddress);
    if (errors.address) setErrors(prev => ({ ...prev, address: null }));

    if (placeId && window.google?.maps?.Geocoder) {
      const geocoder = new window.google.maps.Geocoder();
      try {
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ placeId }, (results, status) => {
            if (status === 'OK' && results[0]) resolve(results[0]);
            else reject(new Error('Geocoding failed'));
          });
        });
        const fields = extractAddressComponents(result.address_components);
        setExtractedAddressFields({
          address_components: result.address_components,
          ...fields,
        });
      } catch (error) {
        console.error('Failed to extract address components:', error);
      }
    } else {
      setExtractedAddressFields({});
    }
  };

  const fetchFrontendPlaceDetails = (placeId, callback) => {
    if (!window.google?.maps?.places) {
      if (callback) callback();
      return;
    }

    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails(
      {
        placeId,
        fields: [
          'place_id', 'name', 'url', 'website', 'formatted_address',
          'international_phone_number', 'formatted_phone_number', 'opening_hours',
          'rating', 'user_ratings_total', 'business_status', 'types', 'address_components',
        ],
      },
      async (result, status) => {
        try {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            const addressFields = extractAddressComponents(result.address_components || []);
            const rawHours = result.opening_hours?.weekday_text || [];
            const hasValidGmbHours = (hours) => Array.isArray(hours) && hours.length > 0;

            const DEFAULT_HOURS_STRING = [
              "Monday: 9:00 AM – 6:00 PM",
              "Tuesday: 9:00 AM – 6:00 PM",
              "Wednesday: 9:00 AM – 6:00 PM",
              "Thursday: 9:00 AM – 6:00 PM",
              "Friday: 9:00 AM – 6:00 PM",
              "Saturday: 9:00 AM – 6:00 PM",
              "Sunday: Closed",
            ].join(" | ");

            const finalHours = hasValidGmbHours(rawHours) ? rawHours.join(' | ') : DEFAULT_HOURS_STRING;

            const businessData = {
              placeId: result.place_id,
              businessName: result.name || '',
              address: result.formatted_address || '',
              formattedAddress: result.formatted_address || '',
              phone: result.international_phone_number || result.formatted_phone_number || '',
              internationalPhone: result.international_phone_number || '',
              website: result.website || '',
              rating: result.rating || '',
              totalRatings: result.user_ratings_total || '',
              hours: finalHours,
              businessStatus: result.business_status || '',
              categories: result.types || [],
              address_components: result.address_components || [],
              city: addressFields.city,
              state: addressFields.state,
              state_code: addressFields.state_code,
              country: addressFields.country,
              country_code: addressFields.country_code,
              postal_code: addressFields.postal_code,
              postalCode: addressFields.postal_code,
              street_number: addressFields.street_number,
              route: addressFields.route,
            };

            setBusinessResult(businessData);
            setViewMode("found");
            trackEvent("business_found_from_phone", {
              business_name: businessData.businessName || "",
              phone: businessData.phone || phone,
              rating: businessData.rating || "",
              total_ratings: businessData.totalRatings || "",
              address: businessData.address || "",
            });

            updateFields({
              placeDetails: businessData,
              displayBusinessName: result.name || '',
              businessUrl: result.website || "",
              noBusinessWebsite: !result.website,
              noGoogleListing: false,
              isGMBSelected: true,
            });

            secureStorage.set("placeDetailsExtract", businessData);
            secureStorage.set("displayBusinessName", result.name || '');
            secureStorage.set("businessUrl", result.website || "");
            fetchAndSetSitemap(result.website); // build website knowledge for createAgent
            autoPickCategory(businessData); // Gemini → pre-select our category
          } else {
            console.error('Frontend place details fetch failed:', status);
          }
        } catch (err) {
          console.error('Error handling places details callback:', err);
        } finally {
          if (callback) callback();
        }
      }
    );
  };

  // Sync state if found view is loaded from external props (e.g. initial load)
  useEffect(() => {
    if (data.placeDetails?.placeId) {
      setViewMode("found");
      setBusinessResult(data.placeDetails);
      setPhone(data.placeDetails.phone || "");
    }
  }, [data.placeDetails?.placeId]);

  const getNationalDigits = (phoneVal, currentDialCode = dialCode) => {
    const digits = (phoneVal || "").replace(/[^0-9]/g, "");
    const code = String(currentDialCode || "").replace(/[^0-9]/g, "");
    if (code && digits.startsWith(code)) {
      return digits.slice(code.length);
    }
    return digits;
  };

  const handlePhoneBlur = () => {
    const nationalDigits = getNationalDigits(phone, dialCode);
    if (nationalDigits.length > 0 && nationalDigits.length !== 10 && viewMode === "lookup") {
      setError("Please enter a valid 10-digit phone number.");
    }
  };

  const handlePhoneChange = (val, countryData) => {
    setPhone(val);
    if (countryData?.dialCode) {
      setDialCode(countryData.dialCode);
    }
    // Flag ko user ki pasand par pin karo — warna +1 wale countries me library
    // wapas US pe chali jati hai.
    if (countryData?.countryCode) {
      setSelectedCountry(countryData.countryCode);
    }
    setError("");
    if (viewMode === "found" || businessResult) {
      setViewMode("lookup");
      setBusinessResult(null);
      clearManualFormState();
    }
  };

  const handleLookup = async () => {
    const nationalDigits = getNationalDigits(phone, dialCode);
    if (!phone || nationalDigits.length !== 10) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const formattedPhone = phone.startsWith("+") ? phone : `+${phone}`;
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL || "/api"}/maps/googleMapLookupByPhone`, {
        phone: formattedPhone,
      });

      if (res.data?.success && res.data?.found && res.data?.business) {
        const b = res.data.business;
        trackEvent("business_phone_lookup_success", {
          phone: formattedPhone,
          business_name: b.name,
        });

        if (window.google?.maps?.places) {
          await new Promise((resolve) => {
            fetchFrontendPlaceDetails(b.placeId, resolve);
          });
        } else {
          const placeData = {
            placeId: b.placeId,
            businessName: b.name,
            phone: b.phone,
            internationalPhone: b.phone,
            website: b.website,
            address: b.address,
            formattedAddress: b.address,
            rating: b.rating,
            totalRatings: b.ratingsTotal,
          };

          setBusinessResult(placeData);
          setViewMode("found");
          trackEvent("business_found_from_phone", {
            business_name: placeData.businessName || "",
            phone: placeData.phone || phone,
            rating: placeData.rating || "",
            total_ratings: placeData.totalRatings || "",
            address: placeData.address || "",
          });

          updateFields({
            placeDetails: placeData,
            displayBusinessName: b.name,
            businessUrl: b.website || "",
            noBusinessWebsite: !b.website,
            noGoogleListing: false,
            isGMBSelected: true,
          });

          secureStorage.set("placeDetailsExtract", placeData);
          secureStorage.set("displayBusinessName", b.name);
          secureStorage.set("businessUrl", b.website || "");
          fetchAndSetSitemap(b.website); // build website knowledge for createAgent
          autoPickCategory(placeData); // Gemini → pre-select our category
        }
      } else {
        setError(
          <>
            Couldn't find this number on Google.<br />
            Double-check it, or add your details yourself.
          </>
        );
        trackEvent("business_phone_lookup_not_found", { phone: formattedPhone });
      }
    } catch (err) {
      console.error("Phone lookup failed:", err);
      setError("Lookup service is temporarily unavailable. Please enter details manually.");
    } finally {
      setLoading(false);
    }
  };

  // Perform lookup when phone meets standard length
  useEffect(() => {
    const nationalDigits = getNationalDigits(phone, dialCode);
    if (nationalDigits.length === 10 && viewMode === "lookup" && !loading) {
      setError("");
      trackEvent("phone_number_entered", { phone: phone });
      const timer = setTimeout(() => {
        handleLookup();
      }, 800);
      return () => clearTimeout(timer);
    } else if (nationalDigits.length > 10 && viewMode === "lookup") {
      setError("Please enter a valid 10-digit phone number.");
    }
  }, [phone, dialCode, viewMode]);

  const handleWebsiteVerification = async (url) => {
    const trimmed = (url || "").trim();
    if (!trimmed || trimmed === "http://" || trimmed === "https://") {
      setIsVerifying(false);
      setIsUrlValid(null);
      return false;
    }

    let formattedUrl = trimmed;
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = "https://" + formattedUrl;
    }
    setWebsite(formattedUrl);

    setIsVerifying(true);
    setIsUrlValid(null);

    try {
      const result = await validateWebsite(formattedUrl);
      if (!result?.valid) {
        setIsUrlValid(false);
        setErrors((prev) => ({
          ...prev,
          website: result?.reason || "Invalid website URL or website unreachable",
        }));
        secureStorage.remove("websiteValidatedUrl");
        return false;
      }

      const finalUrl = (result?.finalUrl || formattedUrl).trim();
      setWebsite(finalUrl);
      setIsUrlValid(true);
      trackEvent("website_validation_success", { website: finalUrl });
      setErrors((prev) => ({ ...prev, website: null }));
      secureStorage.set("businessUrl", finalUrl);
      secureStorage.set("websiteValidatedUrl", finalUrl);
      updateFields({ businessUrl: finalUrl, noBusinessWebsite: false });

      fetchAndSetSitemap(finalUrl);
      return true;
    } catch (err) {
      console.error("Website verification failed:", err);
      setIsUrlValid(false);
      setErrors((prev) => ({
        ...prev,
        website: "Website verification failed. Please check the URL.",
      }));
      return false;
    } finally {
      setIsVerifying(false);
    }
  };

  const handleWebsiteBlur = () => {
    const trimmed = (website || "").trim();
    if (trimmed && !noWebsite && trimmed !== "http://" && trimmed !== "https://") {
      let formattedUrl = trimmed;
      if (!/^https?:\/\//i.test(formattedUrl)) {
        formattedUrl = "https://" + formattedUrl;
        setWebsite(formattedUrl);
      }
      trackEvent("manual_website_entered", { website: formattedUrl });
      handleWebsiteVerification(formattedUrl);
    }
  };

  const handleWebsiteInputChange = (val) => {
    setWebsite(val);
    setIsUrlValid(null);
    if (errors.website) setErrors((prev) => ({ ...prev, website: null }));

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    const trimmed = (val || "").trim();
    if (!trimmed || trimmed === "http://" || trimmed === "https://") {
      setIsVerifying(false);
      setIsUrlValid(null);
      updateFields({ businessUrl: "" });
      secureStorage.remove("businessUrl");
      secureStorage.remove("websiteValidatedUrl");
      return;
    }

    setIsVerifying(true);

    typingTimeoutRef.current = setTimeout(() => {
      handleWebsiteVerification(trimmed);
    }, 800);
  };

  const validateManualForm = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Business Name is required.";
    const nationalManualDigits = getNationalDigits(businessPhone, manualDialCode);
    if (!businessPhone.trim() || nationalManualDigits.length === 0) {
      newErrors.businessPhone = "Business Phone is required.";
    } else if (nationalManualDigits.length !== 10) {
      newErrors.businessPhone = "Please enter a valid 10-digit phone number.";
    }
    if (!address.trim()) newErrors.address = "Address is required.";
    if (!noWebsite) {
      if (!website.trim()) {
        newErrors.website = "Website is required.";
      } else {
        let validWeb = website.trim();
        if (!/^https?:\/\//i.test(validWeb)) {
          validWeb = "https://" + validWeb;
          setWebsite(validWeb);
          updateFields({ businessUrl: validWeb });
          secureStorage.set("businessUrl", validWeb);
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validate: async () => {
      if (viewMode === "lookup") return false;

      if (viewMode === "found") {
        // Gemini ki category bhi event me chahiye — race ka result pakad lo.
        let pickedCategory = "";
        if (classificationPromiseRef.current) {
          try {
            pickedCategory = (await Promise.race([
              classificationPromiseRef.current,
              new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 5000)),
            ])) || "";
          } catch (e) {
            console.warn("StepPhoneLookup validate wait issue:", e?.message);
          }
        }

        /*
         * Continue par poora business snapshot jata hai. Keys DONO raaston
         * (Google-found aur manual) me BILKUL same rakhi hain — is se GA me ek
         * hi schema banta hai aur dono paths compare kiye ja sakte hain. Jo
         * field kisi raste par hoti hi nahi (jaise manual me rating) wo khaali
         * jati hai, gayab nahi — warna GA me schema tut jata hai.
         */
        const details = {
          source: "google",
          is_gmb: true,
          business_name: businessResult?.businessName || "",
          phone: businessResult?.phone || phone || "",
          address: businessResult?.address || "",
          website: businessResult?.website || "",
          has_website: !!businessResult?.website,
          city: businessResult?.city || "",
          state: businessResult?.state || "",
          country: businessResult?.country || "",
          postal_code: businessResult?.postal_code || "",
          rating: businessResult?.rating || "",
          total_ratings: businessResult?.totalRatings || "",
          // secureStorage se — auto-pick fail hone par clearAutoCategory ise
          // synchronously hata deta hai, jabki `data` prop purana ho sakta hai
          // aur GA me ek aisi category bhej deta jo set hui hi nahi.
          category: pickedCategory || String(secureStorage.get("businessType") || ""),
        };

        trackEvent("business_phone_confirmed", {
          ...details,
          // Purani reports isi key par bani hain — hataya nahi.
          no_website: !businessResult?.website,
        });
        trackEvent("business_details_step1_continue", {
          view_mode: "found",
          ...details,
        });
        return true;
      }

      if (viewMode === "manual") {
        const isFormBasicValid = validateManualForm();
        if (!isFormBasicValid) return false;

        if (!noWebsite && website.trim()) {
          if (isUrlValid !== true && !isVerifying) {
            const verified = await handleWebsiteVerification(website);
            if (!verified) return false;
          }
        }

        const placeDetails = {
          ...(data.placeDetails || {}),
          businessName: name.trim(),
          phone: businessPhone.trim(),
          address: address.trim(),
          website: noWebsite ? "" : website.trim(),
          noGoogleListing: true,
          ...extractedAddressFields,
        };

        // Gemini -> pre-select our category. Result event me bhi jata hai.
        const pickedCategory = (await autoPickCategory(placeDetails)) || "";

        updateFields({
          displayBusinessName: name.trim(),
          businessUrl: noWebsite ? "" : website.trim(),
          noBusinessWebsite: noWebsite,
          placeDetails,
          noGoogleListing: true,
          isGMBSelected: false,
        });

        secureStorage.set("placeDetailsExtract", placeDetails);
        secureStorage.set("displayBusinessName", name.trim());
        secureStorage.set("businessUrl", noWebsite ? "" : website.trim());

        // Wahi keys jo "found" raste par jati hain — schema dono taraf ek jaisa.
        const details = {
          source: "manual",
          is_gmb: false,
          business_name: name.trim(),
          phone: businessPhone.trim(),
          address: address.trim(),
          website: noWebsite ? "" : website.trim(),
          has_website: !noWebsite && !!website.trim(),
          city: extractedAddressFields.city || "",
          state: extractedAddressFields.state || "",
          country: extractedAddressFields.country || "",
          postal_code: extractedAddressFields.postal_code || "",
          // Rating sirf Google listing se aati hai — manual me khaali, gayab nahi.
          rating: "",
          total_ratings: "",
          category: pickedCategory,
        };

        trackEvent("business_phone_confirmed", {
          ...details,
          // Purani reports isi key par bani hain — hataya nahi.
          no_website: noWebsite,
        });
        trackEvent("business_details_step1_continue", {
          view_mode: "manual",
          ...details,
        });

        return true;
      }
      return false;
    }
  }));

  // Keep parent formData synced live when typing in manual view mode so hasRequiredData() enables Continue button
  useEffect(() => {
    if (viewMode === "manual") {
      const cleanName = name.trim();
      const cleanPhone = businessPhone.trim();
      const cleanAddr = address.trim();
      const cleanWeb = noWebsite ? "" : website.trim();

      const updatedPlaceDetails = {
        ...(data.placeDetails || {}),
        businessName: name,
        phone: businessPhone,
        address: address,
        website: noWebsite ? "" : website,
        noGoogleListing: true,
      };

      updateFields({
        displayBusinessName: cleanName,
        businessPhone: cleanPhone,
        address: cleanAddr,
        businessUrl: cleanWeb,
        noBusinessWebsite: noWebsite,
        noGoogleListing: true,
        placeDetails: (cleanName || cleanPhone || cleanAddr) ? updatedPlaceDetails : null,
      });
    }
  }, [name, businessPhone, address, website, noWebsite, viewMode]);

  const handleNameChange = (e) => {
    const value = e.target.value
      .replace(/[0-9]/g, "")
      .replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF])/g, "");
    setName(value);
    if (errors.name) setErrors(prev => ({ ...prev, name: null }));
  };

  const clearManualFormState = () => {
    setWebsite("");
    setNoWebsite(false);
    setName("");
    setBusinessPhone("");
    setAddress("");
    setExtractedAddressFields({});
    setIsUrlValid(null);
    setIsVerifying(false);
    setErrors({});
    setBusinessResult(null);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    updateFields({
      placeDetails: null,
      displayBusinessName: "",
      businessUrl: "",
      businessPhone: "",
      noBusinessWebsite: false,
      noGoogleListing: true,
      isGMBSelected: false,
      businessType: "",
      categoryAutoPicked: false,
      sitemapUrls: [],
      selectedSitemapUrls: [],
    });

    secureStorage.remove("businessType");
    secureStorage.remove("categoryAutoPicked");
    secureStorage.remove("businessUrl");
    secureStorage.remove("displayBusinessName");
    secureStorage.remove("placeDetailsExtract");
    secureStorage.remove("websiteValidatedUrl");
    secureStorage.remove("urls");
    secureStorage.remove("selectedSiteMapUrls");
    secureStorage.remove("selectedSiteMapUrls_backup");
  };

  const handleNotMyBusiness = () => {
    // Button unmount hone se pehle focus chhod do — warna browser focus ko
    // scroll container par daal ke uska black focus ring flash karta hai.
    document.activeElement?.blur?.();
    trackEvent("not_my_business_click", {
      searched_phone: phone,
      business_name: businessResult?.businessName || businessResult?.name || "",
    });
    const currentPhone = phone;
    clearManualFormState();
    setViewMode("manual");
    if (currentPhone) {
      setBusinessPhone(currentPhone);
      // Number ke saath uska country bhi le jao — warna Canada ka number
      // manual form me US ke flag ke saath dikhega.
      setManualDialCode(dialCode);
      setManualSelectedCountry(selectedCountry);
      updateFields({ businessPhone: currentPhone, noGoogleListing: true, isGMBSelected: false, placeDetails: null });
    }
  };

  const handleEnterManuallyClick = () => {
    trackEvent("add_details_manually_click", { screen: "phone_lookup", phone: phone });
    const currentPhone = phone;
    clearManualFormState();
    setViewMode("manual");
    if (currentPhone) {
      setBusinessPhone(currentPhone);
      setManualDialCode(dialCode);
      setManualSelectedCountry(selectedCountry);
      updateFields({ businessPhone: currentPhone, noGoogleListing: true, isGMBSelected: false, placeDetails: null });
    }
  };

  // 1. Manual Form View (Screen 4)
  if (viewMode === "manual") {
    return (
      // key: phone-lookup screen se yahan aane par (aur wapas jaane par) React
      // wahi <div> dobara istemal kar leta tha, to heading ka entrance animation
      // chalta hi nahi tha. key se ye screen naye sire se banti hai. State
      // component me hai (DOM me nahi), isliye bhari hui values wahi rehti hain.
      <div key="manual" className={`${styles.phoneLookupContainer} ${styles.manualPhoneLookup}`}>
        <div className={styles.titleBar}>
          <h4 className={styles.lightTitle}>Tell us about</h4>
          <h5 className={styles.boldTitle}>your business</h5>
          <p className={styles.subtitle}>
            Just the basics. Add your website too if you<br />
            have one — it helps us learn your services faster.
          </p>
        </div>

        <div className={styles.manualEntryForm}>
          {/* Website Card */}
          <div className={`${styles.cardInputContainer} ${errors.website ? styles.cardInputError : ""}`}>
            <label className={styles.cardInputLabel}>
              Your Website (URL) {!noWebsite && <span className={styles.req}>*</span>}
            </label>
            <div className={styles.inputWithIcon} style={{ position: "relative" }}>
              <input
                type="text"
                placeholder="https://www.example.com"
                value={noWebsite ? "" : website}
                onChange={(e) => handleWebsiteInputChange(e.target.value)}
                onBlur={handleWebsiteBlur}
                disabled={noWebsite}
                className={styles.cardInputControl}
                style={{ paddingRight: "36px" }}
              />
              {isVerifying ? (
                <span style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center" }}>
                  <svg className={styles.spinAnimation} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6524eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" stroke="#e9d5ff" strokeWidth="2.5" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                </span>
              ) : !noWebsite && isUrlValid === true ? (
                <span className={styles.successIcon} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)" }}>
                  <img src="/svg/check-icon.svg" alt="valid" style={{ width: "16px" }} />
                </span>
              ) : !noWebsite && isUrlValid === false ? (
                <span style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#e11d48", fontWeight: "bold", fontSize: "14px" }}>
                  ✕
                </span>
              ) : null}
            </div>
            {errors.website && <p className={styles.errorText}>{errors.website}</p>}
          </div>

          {/* No Website Checkbox */}
          <div className={styles.phoneLookupCheckbox}>
            <label className={styles.customCheckbox}>
              <input
                type="checkbox"
                checked={noWebsite}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setNoWebsite(checked);
                  trackEvent("no_business_website_toggled", { no_website: checked });
                  if (checked) {
                    setWebsite("");
                    setIsUrlValid(null);
                    setIsVerifying(false);
                    if (errors.website) setErrors(prev => ({ ...prev, website: null }));
                    updateFields({ businessUrl: "", noBusinessWebsite: true });
                    secureStorage.remove("businessUrl");
                    secureStorage.remove("websiteValidatedUrl");
                  }
                }}
              />
              <span className={styles.crossmark}></span>
              <p className={styles.checkLable}>I don't have a website</p>
            </label>
          </div>

          {/* Business Name Card */}
          <div className={`${styles.cardInputContainer} ${errors.name ? styles.cardInputError : ""}`}>
            <label className={styles.cardInputLabel}>
              Business Name <span className={styles.req}>*</span>
            </label>
            <input
              type="text"
              placeholder="Your business name"
              value={name}
              onChange={handleNameChange}
              onBlur={() => { if (name.trim()) trackEvent("manual_business_name_entered", { business_name: name.trim() }); }}
              className={styles.cardInputControl}
            />
            {errors.name && <p className={styles.errorText}>{errors.name}</p>}
          </div>

          {/* Address Card */}
          <div className={`${styles.cardInputContainer} ${errors.address ? styles.cardInputError : ""}`}>
            <label className={styles.cardInputLabel}>
              Address <span className={styles.req}>*</span>
            </label>
            <GoogleAddressInputWithDropdown
              ref={addressInputRef}
              value={address}
              onChange={handleAddressChange}
              onBlur={() => { if (address.trim()) trackEvent("manual_address_entered", { address: address.trim() }); }}
              placeholder="123 King Street, Dallas, TX"
              inputClassName={styles.cardInputControl}
            />
            {errors.address && <p className={styles.errorText}>{errors.address}</p>}
          </div>

          {/* Business Phone Number Card */}
          <div
            className={`${styles.cardInputContainer} ${errors.businessPhone ? styles.cardInputError : ""}`}
            ref={manualPhoneFieldRef}
            onFocusCapture={scrollManualPhoneIntoView}
          >
            <label className={styles.cardInputLabel}>
              Business Phone Number <span className={styles.req}>*</span>
            </label>
            <div className={styles.cardInputPhoneWrap}>
              <PhoneInput
                country={manualSelectedCountry}
                value={withPlus(businessPhone)}
                onChange={(val, countryData) => {
                  setBusinessPhone(val);
                  if (countryData?.dialCode) setManualDialCode(countryData.dialCode);
                  if (countryData?.countryCode) setManualSelectedCountry(countryData.countryCode);
                  if (errors.businessPhone) setErrors(prev => ({ ...prev, businessPhone: null }));
                }}
                placeholder="88XX 77X X55"
                enableSearch={true}
                jumpCursorToEnd={false}
                countryCodeEditable={true}
                inputProps={{
                  onPointerDown: (e) => {
                    // Let native browser cursor placement handle the click position on first tap
                    e.stopPropagation();
                  },
                  onClick: (e) => {
                    const pos = e.target.selectionStart;
                    if (typeof pos === "number") {
                      requestAnimationFrame(() => {
                        try { e.target.setSelectionRange(pos, pos); } catch (_) { }
                      });
                    }
                  },
                  onBlur: () => {
                    const nationalManualDigits = getNationalDigits(businessPhone, manualDialCode);
                    if (nationalManualDigits.length === 10) {
                      trackEvent("manual_business_phone_entered", { phone: businessPhone });
                    }
                  },
                }}
              />
            </div>
            {errors.businessPhone && <p className={styles.errorText}>{errors.businessPhone}</p>}
          </div>
        </div>

        {/* Back Link to Search */}
        <div className={styles.phoneLookupBackLinkWrap} style={{ textAlign: "center", marginTop: "16px" }}>
          <button
            type="button"
            className={styles.notOnGoogleMapsBtn}
            onClick={() => {
              trackEvent("search_by_phone_instead_click", { screen: "manual_form" });
              clearManualFormState();
              setViewMode("lookup");
              updateFields({ noGoogleListing: false });
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              trackEvent("search_by_phone_instead_click", { screen: "manual_form" });
              clearManualFormState();
              setViewMode("lookup");
              updateFields({ noGoogleListing: false });
            }}
            style={{ fontSize: "13px", padding: "8px 18px", boxShadow: "none" }}
          >
            ← Search by phone instead
          </button>
        </div>

        <div className={styles.phoneLookupSpacer} />
      </div>
    );
  }

  // 2. Found / Verification View (Screen 2)
  if (viewMode === "found" && businessResult) {
    return (
      <div className={styles.phoneLookupContainer}>
        <div className={styles.titleBar}>
          <h4 className={styles.lightTitle}>Let's train</h4>
          <h5 className={styles.boldTitle}>your receptionist</h5>
          <p className={styles.subtitle}>
            Start with the number your customers call.<br />
            we'll pull your business details automatically.
          </p>
        </div>

        <div className={styles.formGroupMain} ref={lookupPhoneGroupRef}>
          <label className={styles.inputLabel}>
            Business phone number <span className={styles.req}>*</span>
          </label>

          <div
            className={`${styles.phoneInputWrap} ${styles.fullWidthPhoneInput}`}
            // Wrapper pe (focus/blur upar tak aate hain) — PhoneInput ke apne
            // onFocus/onBlur ko nahi chheda. Screen 1 jaisa hi.
            onFocus={handleLookupPhoneFocus}
            onBlur={handleLookupPhoneBlur}
          >
            <PhoneInput
              country={selectedCountry}
              value={withPlus(phone)}
              onChange={handlePhoneChange}
              placeholder={PHONE_PLACEHOLDER}
              disabled={loading}
              enableSearch={true}
              jumpCursorToEnd={false}
              countryCodeEditable={true}
              inputProps={{
                onPointerDown: (e) => {
                  e.stopPropagation();
                },
                onClick: (e) => {
                  const pos = e.target.selectionStart;
                  if (typeof pos === "number") {
                    requestAnimationFrame(() => {
                      try { e.target.setSelectionRange(pos, pos); } catch (_) { }
                    });
                  }
                },
              }}
            />
          </div>

          {/* Found Place Card */}
          <div className={styles.resultCard}>
            <div className={styles.resultCardLeft}>
              <p className={styles.resultName}>{businessResult.businessName}</p>
              <p className={styles.resultAddr}>{businessResult.address}</p>
            </div>
            <div className={styles.resultCardRight}>
              {businessResult.rating && (
                <span className={styles.resultRating}>
                  <span className={styles.ratingValue}>{businessResult.rating}</span> <span className={styles.star}>★</span> ({businessResult.totalRatings})
                </span>
              )}
              <div style={{ flex: 1 }} />
              <span className={styles.checkMarkCircle}><svg width="20" height="15" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.6836 0.510742C18.0287 0.552089 18.3334 0.706863 18.5811 0.962891L18.5801 0.963867C18.8328 1.21916 18.9743 1.53169 18.999 1.88086L19.002 2.03418C18.9921 2.4363 18.84 2.79271 18.5557 3.07715L18.5498 3.08301L18.5488 3.08203L7.37988 13.9014L7.37598 13.9053C7.18296 14.0885 6.95399 14.2145 6.69531 14.2764L6.69629 14.2773C6.69346 14.2781 6.69033 14.2776 6.6875 14.2783C6.68613 14.2786 6.68496 14.28 6.68359 14.2803L6.68262 14.2793C6.41961 14.345 6.15342 14.3406 5.89355 14.2607L5.88184 14.2578V14.2568C5.62457 14.171 5.39912 14.0286 5.21777 13.8271V13.8262L0.889648 9.00781L0.890625 9.00684C0.617605 8.70396 0.482182 8.33646 0.501953 7.92871L0.516602 7.77441C0.56764 7.42568 0.734581 7.12302 1.00879 6.8877C1.30895 6.61149 1.67924 6.48255 2.08691 6.50195H2.08789C2.49681 6.52199 2.85077 6.69267 3.125 6.99609L3.12695 6.99805L6.40625 10.6572L16.4609 0.921875V0.922852C16.7531 0.633156 17.1211 0.490249 17.5322 0.5L17.6836 0.510742Z" fill="#22C95C" stroke="#22C95C" />
              </svg>
              </span>
            </div>
          </div>

          {/* <div className={styles.resultSeparator} /> */}

          {/* This isn't my business button */}
          <button
            type="button"
            className={styles.notOnGoogleMapsBtn}
            // Sirf onClick — onMouseDown pe view switch karne se bacha hua
            // mouseup/click naye manual form ke field par gir jata tha aur
            // uska focus border flash hota tha.
            onClick={handleNotMyBusiness}
          >
            This isn't my business
          </button>
        </div>

        <div
          className={`${styles.phoneLookupSpacer} ${phoneFocused ? styles.phoneLookupSpacerFocused : ""}`}
        />
      </div>
    );
  }

  // 3. Lookup Input View (Screen 1 & Screen 3)
  return (
    <div className={styles.phoneLookupContainer}>
      <div className={styles.titleBar}>
        <h4 className={styles.lightTitle}>Let's train</h4>
        <h5 className={styles.boldTitle}>your receptionist</h5>
        <p className={styles.subtitle}>
          Start with the number your customers call.<br />
          we'll pull your business details automatically.
        </p>
      </div>

      <div className={styles.formGroupMain} ref={lookupPhoneGroupRef}>
        <label className={styles.inputLabel}>
          Business phone number <span className={styles.req}>*</span>
        </label>

        <div
          className={`${styles.phoneInputWrap} ${styles.fullWidthPhoneInput}`}
          // Wrapper pe (focus/blur upar tak aate hain) — PhoneInput ke apne
          // onFocus/onBlur ko nahi chheda.
          onFocus={handleLookupPhoneFocus}
          onBlur={handleLookupPhoneBlur}
        >
          <PhoneInput
            country={selectedCountry}
            value={withPlus(phone)}
            onChange={handlePhoneChange}
            onBlur={handlePhoneBlur}
            placeholder={usesVobiz() ? "+91 98765 43210" : "+1 202 555 01XX"}
            disabled={loading}
            enableSearch={true}
            jumpCursorToEnd={false}
            countryCodeEditable={true}
            inputProps={{
              onPointerDown: (e) => {
                e.stopPropagation();
              },
              onClick: (e) => {
                const pos = e.target.selectionStart;
                if (typeof pos === "number") {
                  requestAnimationFrame(() => {
                    try { e.target.setSelectionRange(pos, pos); } catch (_) { }
                  });
                }
              },
            }}
          />
        </div>

        {loading && (
          <div className={styles.lookupLoadingContainer}>
            {/* Shimmering Skeleton Card */}
            <div className={`${styles.resultCard} ${styles.shimmerCard}`}>
              <div className={styles.resultCardLeft}>
                <div className={`${styles.skeletonLine} ${styles.skeletonName}`}></div>
                <div className={`${styles.skeletonLine} ${styles.skeletonAddr}`}></div>
              </div>
              <div className={styles.resultCardRight}>
                <div className={`${styles.skeletonLine} ${styles.skeletonRating}`}></div>
                <div style={{ flex: 1 }} />
                <div className={styles.skeletonCheck}></div>
              </div>
            </div>
          </div>
        )}
        {error && <p className={styles.phoneLookupError}>{error}</p>}
      </div>

      {/* Fallback Section */}
      {error ? (
        /* Screen 3: Fallback Card when error exists */
        <div className={styles.fallbackCard}>
          <div className={styles.fallbackTextWrap}>
            <p className={styles.fallbackTitle}>Not on Google?</p>
            <p className={styles.fallbackSub}>No problem.</p>
          </div>
          <button
            type="button"
            className={styles.fallbackOutlineBtn}
            onClick={handleEnterManuallyClick}
            onMouseDown={(e) => {
              e.preventDefault();
              handleEnterManuallyClick();
            }}
            disabled={loading}
          >
            Add details manually <span className={styles.btnArrow}>→</span>
          </button>
        </div>
      ) : (
        /* Screen 1: Default 'I'm not on Google Maps' button when no error */
        <div style={{ textAlign: "center", width: "100%" }}>
          <button
            type="button"
            className={styles.notOnGoogleMapsBtn}
            onClick={handleEnterManuallyClick}
            onMouseDown={(e) => {
              e.preventDefault();
              handleEnterManuallyClick();
            }}
            disabled={loading}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6524eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" fill="#f5f3ff"></path>
              <circle cx="12" cy="10" r="3" fill="#6524eb"></circle>
            </svg>
            I'm not on Google Maps
          </button>
        </div>
      )}

      <div

        className={`${styles.phoneLookupSpacer} ${phoneFocused ? styles.phoneLookupSpacerFocused : ""}`}

      />
    </div>
  );
});

export default StepPhoneLookup;
